import { jwtDecode } from 'jwt-decode';
import apiService from './apiService';
import { API_ROUTES, API_PATHS } from '../config/apiConstants';

// Get the API manager route from environment variable
const apiManagerRoute = API_ROUTES.MANAGER_REGISTER;

// Handle Google OAuth login with user registration/linking
export const handleGoogleLogin = async (credentialResponse, setIsAuthenticated, navigate, setShowAccountLink) => {
    try {
        if (credentialResponse.credential) {
            // Decode the JWT token from Google
            const decoded = jwtDecode(credentialResponse.credential);
            const googleToken = credentialResponse.credential;
            // Extract user information
            const userInfo = {
                email: decoded.email,
                name: decoded.name,
                picture: decoded.picture,
                sub: decoded.sub, // Google's unique user ID
                googleToken: credentialResponse.credential
            };
            
            // Check if this Google account is already linked to a user in your database
            const existingUser = await checkExistingUser(googleToken);
            
            if (existingUser) {
                // User exists, proceed with login
                await completeLogin(existingUser, userInfo, setIsAuthenticated, navigate);
                return { success: true, userInfo: existingUser, isNewUser: false };
            } else {
                // New user or unlinked Google account - show account linking options
                setShowAccountLink(true);
                // Store Google info temporarily
                localStorage.setItem('tempGoogleUserInfo', JSON.stringify(userInfo));
                return { success: true, userInfo, isNewUser: true, needsLinking: true };
            }
        }
    } catch (error) {
        console.error('Google login error:', error);
        throw new Error('Failed to authenticate with Google');
    }
};

// Check if user exists in your database
const checkExistingUser = async (googleToken) => {
    try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_URL}${API_PATHS.LOGIN_WITH_GOOGLE}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                googleToken
            }),
        });

        // Check if response has content before parsing
        const contentType = response.headers.get("content-type");
        let data = null;
        
        if (contentType && contentType.includes("application/json")) {
            const text = await response.text();
            if (text) {
                try {
                    data = JSON.parse(text);
                } catch (parseError) {
                    console.error("JSON parse error:", parseError);
                    return null;
                }
            }
        }

        // Check if login was successful and user exists
        if (response.ok && data && data.success && data.user) {
            return data.user; // Return user data with token
        }
        
        // User not found or other error
        return null;
    } catch (error) {
        console.error('Error checking existing user:', error);
        return null;
    }
};

// Complete the login process
const completeLogin = async (userData, googleInfo, setIsAuthenticated, navigate) => {
    // Store the JWT token from your backend (not the Google token)
    if (userData.token) {
        localStorage.setItem('jwtToken', userData.token);
    }
    
    // Store user profile information for display purposes
    // Prefer server data over Google data when available
    localStorage.setItem('googleUserInfo', JSON.stringify({
        email: userData.email || googleInfo.email,
        name: userData.name || googleInfo.name,
        givenName: userData.givenName || googleInfo.name?.split(' ')[0],
        familyName: userData.familyName || googleInfo.name?.split(' ')[1],
        picture: userData.profilePicture || googleInfo.picture,
        phone: userData.phone,
        sub: googleInfo.sub
    }));
    
    // Store user data from your database
    localStorage.setItem('userData', JSON.stringify(userData));
    
    // Set authentication state
    setIsAuthenticated(true);

    const { checkPHAdmin, getDefaultAuthenticatedHomePath } = await import(
      "./adminAuthority"
    );
    await checkPHAdmin();
    navigate(getDefaultAuthenticatedHomePath());
};

// Link Google account to existing user account
export const linkGoogleAccount = async (username, password, googleInfo, setIsAuthenticated, navigate, setError) => {
    try {
        // First authenticate with existing credentials using your existing endpoint
        const authResponse = await apiService.apiRequest(API_PATHS.MANAGER_LOGIN, 'POST', {
            username,
            password
        });

        // Store the JWT token from the authentication response
        if (authResponse.token) {
            localStorage.setItem('jwtToken', authResponse.token);
        }

        // Now link the Google account to this user
        // Send googleId and email instead of userId
        const linkResponse = await apiService.apiRequest(API_PATHS.LINK_GOOGLE_ACCOUNT, 'POST', {
            googleToken: googleInfo.googleToken
            // Note: No need to send userId - your backend can get it from the JWT token
        });

        // Successfully linked - complete login
        await completeLogin(authResponse, googleInfo, setIsAuthenticated, navigate);
        
        // Clear temporary data
        localStorage.removeItem('tempGoogleUserInfo');
        
        return { success: true };
    } catch (error) {
        // If linking fails, clear the JWT token since the account wasn't properly linked
        if (localStorage.getItem('jwtToken')) {
            localStorage.removeItem('jwtToken');
        }
        setError(error.message);
        throw error;
    }
};

// Create new user account with Google
export const createUserWithGoogle = async (userData, googleInfo, setIsAuthenticated, navigate, setError) => {
    try {
        const response = await apiService.apiRequest(API_PATHS.CREATE_USER_WITH_GOOGLE, 'POST', {
            googleToken: googleInfo.googleToken
        });
        
        // Complete login with new user
        await completeLogin(response.user, googleInfo, setIsAuthenticated, navigate);
        
        // Clear temporary data
        localStorage.removeItem('tempGoogleUserInfo');
        
        return { success: true };
    } catch (error) {
        setError(error.message);
        throw error;
    }
};

// Get current Google user info
export const getGoogleUserInfo = () => {
    const userInfo = localStorage.getItem('googleUserInfo');
    return userInfo ? JSON.parse(userInfo) : null;
};

// Get current user data from database
export const getUserData = () => {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
};

// Check if user is authenticated with Google
export const isGoogleAuthenticated = () => {
    const userInfo = localStorage.getItem('googleUserInfo');
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('jwtToken');
    return !!(userInfo && userData && token);
};

// Logout Google user
export const logoutGoogleUser = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('tempGoogleUserInfo');
};

// Validate Google token (you can extend this to validate with your backend)
export const validateGoogleToken = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) return false;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        // Check if token is expired
        if (decoded.exp < currentTime) {
            logoutGoogleUser();
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Token validation error:', error);
        logoutGoogleUser();
        return false;
    }
};
